import { SetterOrUpdater } from "recoil";
import { UserAuthProviderResponse } from "../../recoil/types";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { TypographySize } from "../shared/Typography/Typography.styles";
import {
  CONTACT_US_URL,
  LANDING_PAGE_URL,
  LOGIN_PAGE_URL,
  MYPROFILE_URL,
} from "../../utils/urlConstants";
import { ColorPalette } from "../../theme";
import { removeCookieAccessToken } from "../../utils/requestUtils";
import { HeaderNavButton } from "./Header.styles";

export interface Header {
  header: React.ReactElement;
  key: string;
}

interface HeaderMenuLinkOptions {
  onToggleHelpPanel?: () => void;
  isHelpPanelOpen?: boolean;
  includeHelpLink?: boolean;
}

export const GetHeaderMenuLinks = (
  typographyColor: keyof ColorPalette,
  fontSize: TypographySize,
  activeHeader: string,
  setActiveHeader: (header: string) => void,
  options: HeaderMenuLinkOptions = {},
): Header[] => {
  const {
    onToggleHelpPanel,
    isHelpPanelOpen = false,
    includeHelpLink = true,
  } = options;
  const isHelpActive = isHelpPanelOpen;
  const getRouteTextColor = (route: string) =>
    !isHelpPanelOpen && activeHeader.includes(route)
      ? "greenMedium"
      : typographyColor;
  const getRouteTextDecoration = (route: string) =>
    !isHelpPanelOpen && activeHeader.includes(route) ? "underline" : "none";
  const helpTypography = GetTypographyContent({
    content: "Help & FAQs",
    size: fontSize,
    padding: "20px 0",
    textColor: `${isHelpActive ? "greenMedium" : typographyColor}`,
    textDecoration: `${isHelpActive ? "underline" : "none"}`,
  });

  const helpLink: Header = {
    header: (
      <HeaderNavButton
        type="button"
        $active={isHelpActive}
        aria-controls="provider-help-panel"
        aria-expanded={isHelpPanelOpen}
        onClick={onToggleHelpPanel}
      >
        {helpTypography}
      </HeaderNavButton>
    ),
    key: "help",
  };

  const links: Header[] = [
    {
      header: GetTypographyContent({
        content: "Home",
        size: fontSize,
        padding: "20px 0",
        link: LANDING_PAGE_URL,
        textColor: getRouteTextColor(LANDING_PAGE_URL),
        textDecoration: getRouteTextDecoration(LANDING_PAGE_URL),
        onClick: () => {
          setActiveHeader(LANDING_PAGE_URL);
        },
      }),
      key: "home",
    },
    {
      header: GetTypographyContent({
        content: "Profile",
        size: fontSize,
        padding: "20px 0",
        link: MYPROFILE_URL,
        textColor: getRouteTextColor(MYPROFILE_URL),
        textDecoration: getRouteTextDecoration(MYPROFILE_URL),
        onClick: () => {
          setActiveHeader(MYPROFILE_URL);
        },
      }),
      key: "profile",
    },
    {
      header: GetTypographyContent({
        content: "Contact us",
        size: fontSize,
        padding: "20px 0",
        link: CONTACT_US_URL,
        textColor: getRouteTextColor(CONTACT_US_URL),
        textDecoration: getRouteTextDecoration(CONTACT_US_URL),
        onClick: () => {
          setActiveHeader(CONTACT_US_URL);
        },
      }),
      key: "contact",
    },
  ];

  if (includeHelpLink && onToggleHelpPanel) {
    links.splice(1, 0, helpLink);
  }

  return links;
};

export const GetLoginHeader = (
  typographyColor: keyof ColorPalette,
  fontSize: TypographySize,
  setProviderInfo: SetterOrUpdater<UserAuthProviderResponse | undefined>,
  loginActive: boolean,
): Header[] =>
  !loginActive
    ? [
        {
          header: GetTypographyContent({
            content: "Login",
            size: fontSize,
            padding: "20px",
            link: LOGIN_PAGE_URL,
            textColor: typographyColor,
            onClick: () => {
              removeCookieAccessToken();
              setProviderInfo(undefined);
            },
          }),
          key: "login",
        },
      ]
    : [];

export const GetMobileActionLinks = (onOpenFeedback: () => void): Header[] => [
  {
    header: GetTypographyContent({
      content: "Give us feedback",
      size: "xlarge",
      padding: "20px 0",
      textColor: "white",
      onClick: onOpenFeedback,
    }),
    key: "give-feedback",
  },
];
